import React, { useCallback, useState } from 'react'
import { usePopper } from 'react-popper'

import cx from 'classnames'
import debounce from 'lodash/debounce'
import { changeFilterGroups } from 'slices/filters'

import { Button, Input, ToolTip } from '@driftt/tide-core'
import { Icon } from '@driftt/tide-icons'

import { DeserializedSavedFilter } from 'api'
import FiltersForm from 'components/FiltersForm'
import FiltersFormPopper from 'components/FiltersFormPopper'
import Portal from 'components/Portal'
import useFilterConfiguration from 'hooks/useFilterConfiguration'
import { useOnClickOutsideWithPortals } from 'hooks/useOnClickOutside'
import { useAppDispatch, useAppSelector } from 'hooks/useRedux'
import { AccountActivitySortValue } from 'modules/accountActivity/domain'
import {
  FilterableEntity,
  FilterConfigAndAvailableValues,
  FilterGroup,
  FilterPage
} from 'utils/filters'
import { formatNumber } from 'utils/formatNumbers'
import { segmentTrack, TrackType } from 'utils/tracking'

import ActiveFilters from './ActiveFilters'

import utilityStyles from '../../styles/utility.module.css'
import styles from './styles.module.css'

const accountActivitySortOptions = {
  //probably better to have this under an asc/desc key too have different labels for each
  [AccountActivitySortValue.MOST_RECENT]: 'most recent activity',
  [AccountActivitySortValue.LEAST_RECENT]: 'least recent',
  [AccountActivitySortValue.ENGAGEMENT_SCORE]: 'engagement score',
  [AccountActivitySortValue.CONTACT_NAME]: 'contact name',
  [AccountActivitySortValue.CONTACT_EMAIL]: 'email',
  [AccountActivitySortValue.NUM_ACTIVITIES]: '# of activities',
  [AccountActivitySortValue.CONTACT_ROLE]: 'contact role',
  [AccountActivitySortValue.LOCATION]: 'contact location',
  [AccountActivitySortValue.ACCOUNT_NAME]: 'account name',
  [AccountActivitySortValue.ACCOUNT_NAME]: 'account name',
  [AccountActivitySortValue.DEAL_NAME]: 'deal name',
  [AccountActivitySortValue.DEAL_AMOUNT]: 'amount',
  [AccountActivitySortValue.DEAL_PROBABILITY]: 'probability',
  [AccountActivitySortValue.DEAL_STAGE]: 'stage name',
  [AccountActivitySortValue.DEAL_FORECAST_CATEGORY]: 'forecast category',
  [AccountActivitySortValue.DEAL_TYPE]: 'type',
  [AccountActivitySortValue.DEAL_OWNER]: 'owner',
  [AccountActivitySortValue.TRENDING]: 'trending'
}

interface SortAndFilterProps {
  selectedSort: AccountActivitySortValue
  descending: boolean
  dataLength: number | undefined
  singularName: string
  pluralName: string
  availableFilters?: FilterableEntity[]
  attributeFilters?: FilterConfigAndAvailableValues[]
  calendarOpenDirection?: 'up' | 'down'
  filterPage: FilterPage
  tooltip?: React.ReactNode
  showSearch?: boolean
  activeFilterId?: number
  onFilterChange?: () => void
  onOpenEditModal?: (filter?: DeserializedSavedFilter, filterGroups?: FilterGroup[]) => void
  onClear?: () => void
}

export const SortAndFilters: React.FC<SortAndFilterProps> = ({
  selectedSort,
  descending,
  dataLength,
  singularName,
  pluralName,
  availableFilters,
  attributeFilters,
  calendarOpenDirection,
  filterPage,
  tooltip,
  showSearch = true,
  activeFilterId,
  onFilterChange,
  onOpenEditModal,
  onClear
}) => {
  const [isFilterCardShown, setIsFilterCardShown] = useState(false)
  const dispatch = useAppDispatch()
  const { searchQuery, filterGroupsByPage } = useAppSelector(({ filters }) => filters)
  const filterGroups = filterGroupsByPage[filterPage]
  const [referenceElement, setReferenceElement] = useState<HTMLElement | null>(null)
  const [popperElement, setPopperElement] = useState<HTMLElement | null>(null)
  const [updatedFilterGroups, setUpdatedFilterGroups] = useState(filterGroups)
  const setSearchQuery = useCallback((query: string): any => dispatch(setSearchQuery(query)), [
    dispatch
  ])

  const debouncedSegmentTrack = useCallback(
    (trackEvent, value: string) => {
      setSearchQuery(value)
      debounce(() => segmentTrack(trackEvent), 250)
    },
    [setSearchQuery]
  )
  const filterConfiguration = useFilterConfiguration(attributeFilters, availableFilters)

  const { styles: popperStyles, attributes, update } = usePopper(referenceElement, popperElement, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        options: {
          offset: [-280, 5]
        }
      }
    ]
  })

  const onChangeFilterGroups = (filterGroups: FilterGroup[], page: FilterPage): any =>
    dispatch(changeFilterGroups({ page, filterGroups }))

  const onSaveFilters = () => {
    setIsFilterCardShown(false)
    onOpenEditModal && onOpenEditModal(undefined, updatedFilterGroups)
  }

  const onApplyFilters = () => {
    setIsFilterCardShown(false)
    onChangeFilterGroups(updatedFilterGroups, filterPage)
  }

  const handleSearchChange = (e: any) => {
    debouncedSegmentTrack(TrackType.SEARCH, e.target.value)
  }

  const handleChangeFilterGroups = (filterGroups: FilterGroup[]) => {
    filterPage && setUpdatedFilterGroups(filterGroups)
    onFilterChange && onFilterChange()
  }

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (isFilterCardShown) {
        e.stopPropagation()
      }
      setIsFilterCardShown(false)
    },
    [isFilterCardShown]
  )

  useOnClickOutsideWithPortals(popperElement, handleClickOutside)

  return (
    <div className={cx(styles.sortAndFilterContainer, styles.sortAndFilterContainerSavedView)}>
      <div className={styles.sortByAndTypeSelector}>
        <div
          className={cx(
            styles.sortByAndTypeSelectorTextMargin,
            styles.sortByAndTypeSelectorTextColorAndSize,
            styles.divider
          )}>
          Showing{' '}
          <span className={utilityStyles.semiBoldWeight}>
            {`${dataLength ? formatNumber(dataLength) : ''} ${
              dataLength === 1 ? singularName : pluralName
            }`}
          </span>
          {accountActivitySortOptions[selectedSort] && (
            <>
              <span> sorted by </span>
              <span className={utilityStyles.semiBoldWeight}>
                {
                  accountActivitySortOptions[
                    selectedSort === AccountActivitySortValue.MOST_RECENT
                      ? descending
                        ? AccountActivitySortValue.MOST_RECENT
                        : AccountActivitySortValue.LEAST_RECENT
                      : selectedSort
                  ]
                }
              </span>
            </>
          )}
          {tooltip && (
            <ToolTip className={styles.tooltip} flow="up" hover tethered component={tooltip}>
              <Icon name="info" width={12} height={12} />
            </ToolTip>
          )}
        </div>
        {availableFilters && availableFilters.length > 0 && filterPage && (
          <ActiveFilters
            onClear={onClear}
            onFilterChange={onFilterChange}
            filterConfiguration={filterConfiguration}
            filterPage={filterPage}
          />
        )}
      </div>
      {availableFilters && availableFilters.length > 0 && filterPage && (
        <div className={styles.filterAndSearch}>
          <div
            className={styles.filterButton}
            ref={setReferenceElement}
            data-ocweb-id="ProspectorFilterToggle"
            onClick={() => {
              update && update()
              setIsFilterCardShown(!isFilterCardShown)
              segmentTrack(TrackType.FILTER_TOGGLED)
            }}>
            <Button className={cx(isFilterCardShown && styles.focusedFilterButton)} type="utility">
              <Icon name="pb-filter" />
              Filter
            </Button>
          </div>
          <Portal>
            <FiltersFormPopper
              ref={setPopperElement}
              attributes={attributes}
              popperStyles={popperStyles.popper}
              isFilterCardShown={isFilterCardShown}
              onSaveFilters={onSaveFilters}
              onApplyFilters={onApplyFilters}
              onCancel={() => setIsFilterCardShown(false)}>
              <FiltersForm
                initialGroups={filterGroups}
                onChangeFilterGroups={handleChangeFilterGroups}
                calendarOpenDirection={calendarOpenDirection}
                filterConfiguration={filterConfiguration}
                activeFilterId={activeFilterId}
                onClose={() => setIsFilterCardShown(false)}
              />
            </FiltersFormPopper>
          </Portal>
          {showSearch && (
            <Input
              data-ocweb-id="ProspectorSearchAction"
              className={styles.searchInput}
              type="search"
              placeholder="Search by account name..."
              size="small"
              defaultValue={searchQuery}
              onChange={handleSearchChange}
            />
          )}
        </div>
      )}
    </div>
  )
}
